import React, { useMemo, useState } from "react";

import { Box } from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import {
  Button,
  DateInput,
  PageHeader,
  SearchEmpty,
  DataTable,
} from "components";

import { usePermissions } from "hooks/permission";
import { useToast } from "hooks/toast";

import { useAppSelector } from "store/rootReducer";

import { colors } from "global/globalStyle";

import { ContainerLeftMenu } from "pages/app/Administration/styles";
import { ReportMenu } from "pages/app/ReportsKenta/ReportMenu";

import { IDataTableColumn } from "components/DataTable/interface";

import { ReportsService } from "services/reportsService";

import { FORMS_RELATORIOS } from "utils/entities/permission";

import { handleChangeFinishDate, handleChangeStartDate } from "./constants";
import { Container, ContainerBody, ContainerList } from "./styles";

export const TranscriptionReport: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<string | null>(
    new Date().toString(),
  );
  const [finishDate, setFinishDate] = useState<string | null>(
    new Date().toString(),
  );
  const [startDateError, setStartDateError] = useState<boolean>(false);
  const [finishDateError, setFinishDateError] = useState<boolean>(false);
  const [list, setList] = useState<Array<any>>([]);

  const { user } = useAppSelector((state) => state.user);

  const startDateErrorFlag: boolean = false;
  const finishDateErrorFlag: boolean = false;
  const { addToast } = useToast();

  const { read } = usePermissions(FORMS_RELATORIOS.TRASCRIPTION);

  const columns: IDataTableColumn[] = useMemo(
    () => [
      {
        id: "duracaoMin",
        label: t("reports.transcriptions.durationMin"),
        width: "10%",
        align: "center",
      },
      {
        id: "inicio",
        label: t("common.start"),
        width: "13%",
        align: "center",
        format: (value) => {
          if (!value && value !== 0) return t("common.notApplicable");
          const date = dayjs(value);
          return (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "4px" }}
            >
              <div>{date.format("DD/MM/YYYY")}</div>
              <div style={{ fontSize: "11px", color: colors.neutral_gray_40 }}>
                {date.format("HH:mm:ss")}
              </div>
            </div>
          );
        },
      },
      {
        id: "fim",
        label: t("common.end"),
        width: "13%",
        align: "center",
        format: (value) => {
          if (!value) return t("common.notApplicable");
          const date = dayjs(value);
          return (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "4px" }}
            >
              <div>{date.format("DD/MM/YYYY")}</div>
              <div style={{ fontSize: "11px", color: colors.neutral_gray_40 }}>
                {date.format("HH:mm:ss")}
              </div>
            </div>
          );
        },
      },
      {
        id: "tempoProcessamentoSeg",
        label: t("reports.transcriptions.processingSeconds"),
        width: "14%",
        align: "center",
      },
      {
        id: "tipo",
        label: t("reports.transcriptions.type"),
        width: "18%",
        align: "center",
      },
      {
        id: "produto",
        label: t("reports.transcriptions.product"),
        width: "15%",
        align: "center",
      },
      {
        id: "tentativas",
        label: t("reports.transcriptions.attempts"),
        width: "10%",
        align: "center",
      },
    ],
    [t],
  );

  const handleSearch = async () => {
    try {
      setLoading(true);

      if (
        !startDateErrorFlag &&
        startDate !== null &&
        !finishDateErrorFlag &&
        finishDate !== null
      ) {
        const startDateAux = new Date(startDate);
        const finishDateAux = new Date(finishDate);

        if (startDateAux > finishDateAux) {
          addToast({
            type: "warning",
            title: t("common.warning"),
            description: t("reports.startDateGreater"),
          });
          return;
        }

        const customerId = user?.customerId;

        if (customerId === null) {
          addToast({
            type: "error",
            title: t("common.warning"),
            description: t("reports.customerNotFound"),
          });
          return;
        }

        const listService = await ReportsService.getTranscriptionKentaAI(
          customerId,
          startDateAux,
          finishDateAux,
        );
        setList(listService.data || []);
      } else {
        addToast({
          type: "warning",
          title: t("common.warning"),
          description: t("reports.fillDates"),
        });
        return;
      }
    } catch {
      addToast({
        type: "error",
        title: t("common.warning"),
        description: t("common.unexpectedError"),
      });
      return;
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box style={{ display: "flex" }}>
      <ContainerLeftMenu>
        <ReportMenu />
      </ContainerLeftMenu>
      <Box style={{ width: "100%" }}>
        <PageHeader
          quantity={list.length > 0 ? list.length : 0}
          id="header"
          data-cy="header"
          label={t("reports.transcriptions.report")}
          background="secondary"
          addClick={undefined}
          disabled={false}
        />
        <ContainerBody>
          {read ? (
            <>
              <Container>
                <DateInput
                  label={t("labels.startDate")}
                  value={startDate}
                  setValue={(e) =>
                    handleChangeStartDate(e, setStartDate, setStartDateError)
                  }
                  error={startDateError}
                  required
                />
                <DateInput
                  label={t("labels.endDate")}
                  value={finishDate}
                  setValue={(e) =>
                    handleChangeFinishDate(e, setFinishDate, setFinishDateError)
                  }
                  error={finishDateError}
                  required
                />
                <Button onClick={handleSearch} marginTop="26px">
                  {t("common.search")}
                </Button>
              </Container>
              <ContainerList>
                <DataTable
                  columns={columns}
                  rows={list}
                  loading={loading}
                  emptyMessage={t("reports.transcriptions.none")}
                  maxHeight="60vh"
                  stickyHeader
                  striped
                />
              </ContainerList>
            </>
          ) : (
            <SearchEmpty
              textMessage={t("common.noPermissionForProfile")}
              typeIcon={"avatar"}
              marginTop="30px"
            />
          )}
        </ContainerBody>
      </Box>
    </Box>
  );
};
